import {
  NATAL_SAMPLE_HOMES,
  SAMPLE_CHURCH_HOME,
  SAMPLE_CHURCH_NUMBER,
  sampleAddress,
  sampleLocation,
} from './geography';
import { CHURCH_DEMO_MINISTRIES } from './churchDemo';
import { sampleAudiences, sampleVisitors } from './sampleMembership';
import { WEEKDAY_LONG, addDays, hashString, normalize, startOfWeek, weekdayOf } from './dates';
import { DEFAULT_CRITERIA, generateSchedule } from './generator';
import { EMPTY_PROFILE } from './members';
import { legacyWeeklyRule } from './recurrence';
import { NO_ROLE_RULES } from './roleRules';
import { LEGAL_VERSION, privacyDueDate } from './legal';
import { emptyRegistrationData } from './registrations';
import { DEFAULT_JOURNEY, DEFAULT_VISITOR_RETENTION } from './visitors';
import { buildLookup } from './schedule';
import { swapCandidates } from './swaps';
import type {
  Address,
  AvailState,
  Blackout,
  ChurchStatus,
  CivilStatus,
  CoupleMode,
  DomainState,
  JoinedVia,
  LogEntry,
  Member,
  Ministry,
  Organization,
  PrivacyRequest,
  Registration,
  RegistrationLink,
  Role,
  ScheduleEvent,
  Settings,
  Sex,
  SwapRequest,
} from './types';

/**
 * Dados de demonstração — os mesmos do protótipo do Claude Design (ministérios,
 * membros, disponibilidades e séries de cultos), com as datas ancoradas na semana
 * atual para que a demonstração faça sentido em qualquer dia.
 *
 * São fictícios e servem para explorar a interface enquanto não há backend. Nada aqui
 * é carregado quando a aplicação estiver ligada a dados reais.
 */

export const DEMO_ADMIN_ID = 'm1';
export const DEMO_LEADER_ID = 'm2';
/** Gabriel Souza, Líder de Função da Projeção (Multimídia). */
export const DEMO_ROLE_LEADER_ID = 'm13';
export const DEMO_MEMBER_ID = 'm3';
/** Daniel Oliveira, Administrador da organização de exemplo. */
export const DEMO_ORG_ADMIN_ID = 'm17';
/**
 * Versão dos Termos e da Política que os acessos de demonstração aceitaram: a em vigor
 * (a aplicação ainda está na primeira, 1.0). Quem nunca aceitou (o Administrador da
 * plataforma criado pelo bootstrap) vê o primeiro aceite.
 */
export const DEMO_TERMS_VERSION = LEGAL_VERSION;

/** A organização de exemplo, que reúne todos os dados e usuários da demonstração. */
export const SAMPLE_ORG: Organization = {
  id: 'org-esperanca',
  name: 'Igreja Comunidade Esperança',
  slug: 'comunidade-esperanca',
  city: 'Natal',
  uf: 'RN',
  address: sampleAddress(SAMPLE_CHURCH_HOME, SAMPLE_CHURCH_NUMBER),
  location: sampleLocation(SAMPLE_CHURCH_HOME, SAMPLE_CHURCH_NUMBER, Date.parse('2025-03-02T12:00:00Z')),
  color: '#2563EB',
  status: 'active',
  createdAt: '2025-03-02',
};

/** Token do link de cadastro de exemplo (fictício: o endereço não existe fora da demonstração). */
export const SAMPLE_REGISTRATION_TOKEN = 'k7q2m9x4';
/** O link público da ficha de visitante do exemplo (`/{slug}/visitante/{token}`). */
export const SAMPLE_VISITOR_TOKEN = 'v3n8p2q6';

/**
 * As configurações de uma igreja nova. O limite padrão é de 2 escalas por pessoa no mês:
 * numa igreja de verdade, o serviço é dividido entre muita gente, e o Administrador sobe o
 * limite em Configurações quando faltar gente (os dados de exemplo usam `SAMPLE_SETTINGS`).
 */
export const DEFAULT_SETTINGS: Settings = {
  maxPerMonth: 2,
  minGapDays: 6,
  allowUnset: true,
  notifyPublished: true,
  notifyConflicts: true,
  notifyReminders: true,
  reminderDaysBefore: 2,
  reminderChannels: ['email'],
  nudgePending: true,
  nudgeAfterDays: 2,
  nudgeMax: 2,
  leaderDigest: true,
  visitorJourney: DEFAULT_JOURNEY.map((s) => ({ ...s })),
  visitorRetentionMonths: DEFAULT_VISITOR_RETENTION,
};

/**
 * As configurações da igreja de exemplo. O limite mensal acompanha o volume dos dados de
 * exemplo: ~165 posições por mês para 22 pessoas dá uma média de 7 a 8 escalas por pessoa.
 * Com menos que isso, a geração deixaria vagas abertas por regra, não por falta de gente.
 */
export const SAMPLE_SETTINGS: Settings = { ...DEFAULT_SETTINGS, maxPerMonth: 8 };

const MINISTRIES: Ministry[] = [
  {
    id: 'rec',
    name: 'Recepção',
    description: 'Acolhimento e organização da entrada durante os cultos.',
    color: '#2563EB',
    icon: 'door',
    leaderIds: ['m2'],
    welcome: true,
    roles: [
      {
        id: 'portaria',
        name: 'Portaria',
        qty: 2,
        allowedSex: 'any',
        rules: ['Chegar 30 min antes'],
        leaderIds: [],
        icon: 'door',
        ...NO_ROLE_RULES,
      },
      {
        id: 'guardioes',
        name: 'Guardiões',
        qty: 2,
        allowedSex: 'any',
        rules: ['Maiores de 18 anos'],
        leaderIds: [],
        icon: 'guard',
        ...NO_ROLE_RULES,
      },
      {
        id: 'acolhimento',
        name: 'Acolhimento',
        qty: 2,
        allowedSex: 'any',
        rules: ['Preferir casais'],
        leaderIds: [],
        icon: 'users',
        ...NO_ROLE_RULES,
      },
    ],
  },
  {
    id: 'lou',
    name: 'Louvor',
    description: 'Música e ministração nos cultos.',
    color: '#16A34A',
    icon: 'music',
    leaderIds: ['m17'],
    welcome: false,
    roles: [
      {
        id: 'vocal',
        name: 'Vocal',
        qty: 2,
        allowedSex: 'any',
        rules: ['Ensaio no sábado'],
        leaderIds: [],
        icon: 'mic',
        ...NO_ROLE_RULES,
      },
      {
        id: 'teclado',
        name: 'Teclado',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'keyboard',
        ...NO_ROLE_RULES,
      },
      {
        id: 'bateria',
        name: 'Bateria',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'drums',
        ...NO_ROLE_RULES,
      },
    ],
  },
  {
    id: 'mul',
    name: 'Multimídia',
    description: 'Projeção, som e transmissão.',
    color: '#0891B2',
    icon: 'monitor',
    leaderIds: ['m21'],
    welcome: false,
    roles: [
      {
        id: 'projecao',
        name: 'Projeção',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: ['m13'],
        icon: 'projector',
        ...NO_ROLE_RULES,
      },
      {
        id: 'som',
        name: 'Som',
        qty: 1,
        allowedSex: 'any',
        rules: ['Treinamento concluído'],
        leaderIds: [],
        icon: 'audio',
        ...NO_ROLE_RULES,
      },
      {
        id: 'transmissao',
        name: 'Transmissão',
        qty: 1,
        allowedSex: 'any',
        rules: [],
        leaderIds: [],
        icon: 'live',
        ...NO_ROLE_RULES,
      },
    ],
  },
  {
    id: 'inf',
    name: 'Infantil',
    description: 'Cuidado com as crianças durante os cultos.',
    color: '#DB2777',
    icon: 'smile',
    leaderIds: ['m5'],
    welcome: false,
    roles: [
      {
        id: 'professor',
        name: 'Professor(a)',
        qty: 2,
        allowedSex: 'any',
        rules: ['Somente cultos da manhã'],
        leaderIds: [],
        icon: 'teacher',
        ...NO_ROLE_RULES,
      },
      {
        id: 'bercario',
        name: 'Berçário',
        qty: 1,
        allowedSex: 'F',
        rules: [],
        leaderIds: [],
        icon: 'nursery',
        ...NO_ROLE_RULES,
      },
    ],
  },
];

/**
 * Regras avançadas de exemplo: Som e Projeção são críticas, Projeção exige 3 escalas
 * anteriores, Bruno Teixeira está em treinamento no Som e João Silva e Lucas Ferreira não
 * servem juntos quando um deles está na Portaria. Entram depois do histórico de exemplo,
 * que é gerado como se as regras ainda não existissem (senão ninguém teria experiência).
 */
const ADVANCED_RULES: Record<string, Partial<Role>> = {
  projecao: { critical: true, minExperience: 3 },
  som: { critical: true, trainees: ['m21'] },
  portaria: { blockedPairs: [['m3', 'm6']] },
};

function withAdvancedRules(ministries: Ministry[]): Ministry[] {
  return ministries.map((m) => ({
    ...m,
    roles: m.roles.map((r) => ({ ...r, ...structuredClone(ADVANCED_RULES[r.id] ?? {}) })),
  }));
}

/**
 * Par que serve junto de exemplo: Paula Ribeiro e Marta Almeida, amigas, no Acolhimento.
 * Entra depois das escalas de exemplo (geradas sem ele), para não mudar quem está em cada
 * uma; a próxima geração já as escala juntas.
 */
const SAMPLE_TOGETHER: Record<string, [string, string][]> = {
  acolhimento: [['m2', 'm16']],
};

function withSamplePairs(ministries: Ministry[]): Ministry[] {
  return ministries.map((m) => ({
    ...m,
    roles: m.roles.map((r) =>
      SAMPLE_TOGETHER[r.id] ? { ...r, togetherPairs: structuredClone(SAMPLE_TOGETHER[r.id]!) } : r
    ),
  }));
}

/**
 * Escala do casal de exemplo: Marcos e Carla Andrade servem juntos; João e Ana Silva
 * revezam os cultos. Os demais casais ficam sem preferência.
 */
const COUPLE_MODES: Record<string, CoupleMode> = {
  m1: 'together',
  m5: 'together',
  m3: 'alternate',
  m4: 'alternate',
};

/** [id, nome, idade, sexo, cônjuge, funções, disponibilidade (dom → sáb, manhã/tarde/noite)] */
const PEOPLE: [string, string, number, Sex, string, Record<string, string[]>, string][] = [
  [
    'm1',
    'Marcos Andrade',
    45,
    'M',
    'm5',
    { rec: ['guardioes'], mul: ['som'] },
    'yny nnn nnn nny nnn nnn uuu',
  ],
  ['m2', 'Paula Ribeiro', 38, 'F', 'm9', { rec: ['portaria', 'acolhimento'] }, 'yyy nnn nnn nny nnn nnn nyy'],
  [
    'm3',
    'João Silva',
    29,
    'M',
    'm4',
    { rec: ['guardioes'], mul: ['projecao'] },
    'yny nnn nnn nnn nnn nnn nny',
  ],
  [
    'm4',
    'Ana Silva',
    27,
    'F',
    'm3',
    { rec: ['portaria', 'acolhimento'], inf: ['professor'] },
    'yny nnn nnn nny nnn nnn nny',
  ],
  [
    'm5',
    'Carla Andrade',
    43,
    'F',
    'm1',
    { inf: ['professor', 'bercario'], rec: ['acolhimento'] },
    'ynn uuu uuu uuu uuu uuu uuu',
  ],
  [
    'm6',
    'Lucas Ferreira',
    24,
    'M',
    '',
    { lou: ['bateria'], mul: ['transmissao'] },
    'nny nnn nnn nny nnn nny nny',
  ],
  ['m7', 'Beatriz Costa', 31, 'F', '', { lou: ['vocal'], inf: ['professor'] }, 'yyy uuu uuu uuu uuu uuu uuu'],
  ['m8', 'Rafael Mendes', 35, 'M', 'm10', { rec: ['portaria', 'guardioes'] }, 'yny nnn nnn nny nnn nnn nnn'],
  [
    'm9',
    'Eduardo Ribeiro',
    40,
    'M',
    'm2',
    { rec: ['guardioes'], mul: ['som'] },
    'yyn nnn nnn nny nnn nnn nny',
  ],
  [
    'm10',
    'Juliana Mendes',
    33,
    'F',
    'm8',
    { lou: ['vocal'], rec: ['acolhimento'] },
    'yny nnn nnn nnn nnn nnn nnn',
  ],
  [
    'm11',
    'Thiago Rocha',
    26,
    'M',
    '',
    { lou: ['teclado'], mul: ['projecao'] },
    'yny uuu uuu uuy uuu uuu uuy',
  ],
  ['m12', 'Fernanda Lima', 30, 'F', '', { inf: ['bercario', 'professor'] }, 'ynn nnn nnn nnn nnn nnn nnn'],
  ['m13', 'Gabriel Souza', 22, 'M', '', { mul: ['projecao', 'transmissao'] }, 'yyy nnn nnn nny nnn nnn yyy'],
  ['m14', 'Larissa Martins', 28, 'F', '', { lou: ['vocal'] }, 'nny nnn nnn nny nnn nnn nny'],
  ['m15', 'Pedro Almeida', 50, 'M', 'm16', { rec: ['portaria'] }, 'ynn nnn nnn nny nnn nnn nnn'],
  [
    'm16',
    'Marta Almeida',
    48,
    'F',
    'm15',
    { rec: ['acolhimento'], inf: ['bercario'] },
    'ynn nnn nnn nnn nnn nnn nnn',
  ],
  ['m17', 'Daniel Oliveira', 34, 'M', '', { lou: ['teclado', 'vocal'] }, 'yny uuu uuu uuy uuu uuu uuy'],
  ['m18', 'Camila Duarte', 25, 'F', '', { rec: ['portaria'], mul: ['som'] }, 'uuu uuu uuu uuu uuu uuu uuu'],
  [
    'm19',
    'Mateus Carvalho',
    37,
    'M',
    '',
    { rec: ['guardioes'], lou: ['bateria'] },
    'yny nnn nnn nny nnn nnn nny',
  ],
  ['m20', 'Renata Gomes', 41, 'F', '', { inf: ['professor'] }, 'yyn nnn nnn nnn nnn nnn nnn'],
  ['m21', 'Bruno Teixeira', 29, 'M', '', { mul: ['som', 'transmissao'] }, 'nyy nnn nnn nny nnn nnn yyy'],
  ['m22', 'Sofia Barros', 23, 'F', '', { lou: ['vocal'], inf: ['professor'] }, 'yny nnn nnn nnn nnn nnn nny'],
];

const AVAIL: Record<string, AvailState> = { y: 'available', n: 'unavailable', u: 'unset' };

/** Data de nascimento estável a partir da idade, para a idade exibida bater com a tabela. */
function birthFor(id: string, age: number, today: string): string {
  const month = (hashString(id) % 12) + 1;
  const day = (hashString(`${id}d`) % 28) + 1;
  const year = Number(today.slice(0, 4)) - age;
  const candidate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  // Se o aniversário ainda não chegou neste ano, a idade seria um a menos.
  return candidate.slice(5) > today.slice(5) ? `${year - 1}${candidate.slice(4)}` : candidate;
}

/** Membros sem e-mail nos dados de exemplo: aparecem em Acessos como "Sem e-mail". */
const NO_EMAIL = new Set(['m20', 'm21', 'm22']);

/** E-mail de exemplo a partir do nome ("João Silva" → joao.silva@exemplo.com). */
function emailFor(id: string, name: string): string {
  return NO_EMAIL.has(id) ? '' : `${normalize(name).replace(/\s+/g, '.')}@exemplo.com`;
}

/**
 * Telefone de exemplo, estável a partir do id: celular de São Paulo, um fixo (m15) para
 * mostrar a outra máscara e Camila Duarte (m18) sem telefone — o botão do WhatsApp some.
 */
function phoneFor(id: string): string {
  if (id === 'm18') return '';

  const n = String(hashString(`${id}tel`) % 100_000_000).padStart(8, '0');

  return id === 'm15' ? `113${n.slice(1)}` : `119${n}`;
}

function members(today: string): Member[] {
  const blackouts: Record<string, Blackout[]> = {
    m8: [{ id: 'b1', from: addDays(today, 12), to: addDays(today, 22), reason: 'Férias' }],
    m3: [{ id: 'b2', from: addDays(today, 26), to: addDays(today, 28), reason: 'Viagem' }],
  };

  return PEOPLE.map(([id, name, age, sex, spouse, roles, avail]) => ({
    id,
    inactiveSince: null,
    selfUpdatedAt: null,
    // Os dois Administradores dos dados de exemplo: a plataforma (Marcos) e a organização (Daniel).
    admin: id === DEMO_ADMIN_ID || id === DEMO_ORG_ADMIN_ID,
    photo: null,
    name,
    nickname: '',
    email: emailFor(id, name),
    phone: phoneFor(id),
    birthDate: birthFor(id, age, today),
    sex,
    spouseId: spouse || null,
    spouseText: id === 'm7' ? 'Thiago' : '',
    coupleMode: COUPLE_MODES[id] ?? null,
    roles,
    availability: avail
      .replace(/ /g, '')
      .split('')
      .map((c) => AVAIL[c] ?? 'unset'),
    blackouts: blackouts[id] ?? [],
    ...profileFor(id, name, age, spouse, today),
  }));
}

/**
 * A casa de cada domicílio de exemplo (o casal mora junto): o índice em
 * `NATAL_SAMPLE_HOMES` (ruas reais de Natal, com a coordenada da rua). O bairro é o que a
 * Distribuição geográfica da Membresia conta; Larissa (m14) mora em Parnamirim.
 */
const HOMES: Record<string, number> = {
  m1: 0,
  m3: 1,
  m13: 2,
  m2: 3,
  m7: 4,
  m17: 5,
  m22: 6,
  m15: 7,
  m18: 8,
  m6: 9,
  m11: 10,
  m8: 11,
  m12: 12,
  m19: 13,
  m20: 14,
  m21: 17,
  m14: 21,
};

const PROFESSIONS = [
  'Professor(a)',
  'Enfermeiro(a)',
  'Engenheiro(a)',
  'Analista de sistemas',
  'Comerciante',
  'Advogado(a)',
  'Estudante',
  'Contador(a)',
  'Designer',
  'Vendedor(a)',
] as const;
const VIAS: JoinedVia[] = ['baptism', 'baptism', 'profession', 'transfer', 'acclamation', 'baptism'];
const RELATIONS = ['Mãe', 'Pai', 'Irmão(ã)', 'Amigo(a)'] as const;
const CIVIL_BY_AGE: [number, CivilStatus][] = [[46, 'divorced']];
/**
 * Quem entrou na igreja no último ano (dias antes de hoje): dá movimento à evolução da
 * Membresia. Não mexe na geração — nenhuma função de exemplo exige tempo de casa.
 */
const RECENT_JOIN_DAYS: Record<string, number> = {
  m13: 40,
  m22: 115,
  m18: 190,
  m21: 250,
  m14: 300,
  m19: 340,
};

/** Quem não é "Membro em comunhão" nos dados de exemplo. */
const CHURCH_STATUS_OF: Record<string, ChurchStatus> = { m22: 'congregant', m20: 'transferring' };

/**
 * Dados complementares de exemplo (fictícios), estáveis a partir do id: estado civil pelo
 * cônjuge, profissão, situação e tempo de igreja, endereço em Natal com a coordenada da rua
 * (`HOMES`) e um contato de
 * emergência — o cônjuge, quando há.
 */
function profileFor(id: string, name: string, age: number, spouse: string, today: string) {
  const h = hashString(`${id}perfil`);
  const year = Number(today.slice(0, 4));
  const since = Math.min(age - 14, 1 + (h % 18));
  const via = VIAS[h % VIAS.length]!;
  // O casal mora junto: quem não tem casa própria usa a do cônjuge.
  const owner = HOMES[id] === undefined ? spouse : id;
  const home = NATAL_SAMPLE_HOMES[HOMES[owner] ?? 4]!;
  const homeHash = hashString(`${owner}casa`);
  const number = String(10 + (homeHash % 1900));
  const spouseName = PEOPLE.find(([pid]) => pid === spouse)?.[1];
  const civil: CivilStatus = spouse ? 'married' : (CIVIL_BY_AGE.find(([min]) => age >= min)?.[1] ?? 'single');
  const status: ChurchStatus = CHURCH_STATUS_OF[id] ?? 'active';
  const month = String((h % 12) + 1).padStart(2, '0');
  const address: Address = sampleAddress(
    home,
    number,
    homeHash % 3 === 0 ? `Apto ${10 + (homeHash % 90)}` : ''
  );

  const recent = RECENT_JOIN_DAYS[id];
  const memberSince = recent === undefined ? `${year - since}-${month}-10` : addDays(today, -recent);

  return {
    ...EMPTY_PROFILE,
    civilStatus: civil,
    profession: PROFESSIONS[h % PROFESSIONS.length]!,
    churchStatus: status,
    memberSince,
    joinedVia: via,
    baptismDate: via === 'baptism' ? memberSince : `${year - since - 3}-${month}-20`,
    originChurch: via === 'transfer' || via === 'acclamation' ? 'Igreja Batista da Lapa' : '',
    address,
    location: sampleLocation(home, number, Date.parse(`${today}T12:00:00Z`)),
    emergency: {
      name:
        spouseName ??
        `${RELATIONS[h % RELATIONS.length] === 'Mãe' ? 'Maria' : 'José'} ${name.split(' ').at(-1)}`,
      relation: spouseName ? 'Cônjuge' : RELATIONS[h % RELATIONS.length]!,
      phone: `119${String(hashString(`${id}sos`) % 100_000_000).padStart(8, '0')}`,
    },
  };
}

/**
 * Pedido de privacidade de exemplo (Configurações › Privacidade): Rafael Mendes pediu a
 * exclusão da conta há 3 dias — o prazo de 15 dias está correndo.
 */
function samplePrivacyRequests(today: string, now: number): PrivacyRequest[] {
  const sent = addDays(today, -3);

  return [
    {
      id: 'priv1',
      memberId: 'm8',
      kind: 'deletion',
      note: 'Vou me mudar de cidade e não vou mais servir aqui.',
      status: 'open',
      createdAt: now - 3 * 86_400_000,
      dueDate: privacyDueDate(sent),
      resolvedAt: null,
      resolvedBy: '',
      response: '',
      anonymized: false,
    },
  ];
}

/**
 * Cadastros de exemplo que chegaram pelo link: dois aguardando aprovação — Paula Ribeiro
 * com nome e telefone iguais aos de quem já está na base (possível duplicidade) — e, no
 * Histórico, um aprovado e um recusado.
 */
function sampleRegistrations(people: Member[], today: string, now: number): Registration[] {
  const hour = 3_600_000;
  const paula = people.find((m) => m.id === DEMO_LEADER_ID);
  const base = emptyRegistrationData();

  return [
    {
      id: 'reg1',
      data: {
        ...base,
        name: 'Helena Prado',
        birthDate: `${Number(today.slice(0, 4)) - 27}-04-18`,
        sex: 'F',
        civilStatus: 'single',
        profession: 'Fisioterapeuta',
        phone: '11987654321',
        email: 'helena.prado@exemplo.com',
        address: {
          cep: '04101300',
          street: 'Rua Vergueiro',
          number: '3185',
          complement: 'Apto 52',
          district: 'Vila Mariana',
          city: 'São Paulo',
          uf: 'SP',
        },
        churchStatus: 'congregant',
        memberSince: null,
        joinedVia: null,
        baptismDate: null,
        ministryIds: ['lou', 'inf'],
        notes: 'Canto no coral da faculdade e já ajudei no berçário de outra igreja.',
      },
      status: 'pending',
      submittedAt: now - 5 * hour,
      decidedAt: null,
      decidedBy: '',
      reason: '',
      memberId: null,
      linked: false,
      mergedFields: [],
      photo: null,
    },
    {
      id: 'reg2',
      data: {
        ...base,
        name: 'Paula Ribeiro',
        birthDate: paula?.birthDate ?? null,
        sex: 'F',
        civilStatus: 'married',
        profession: 'Professora',
        phone: paula?.phone ?? '',
        email: 'paularibeiro.novo@exemplo.com',
        churchStatus: 'active',
        memberSince: '2015-03-10',
        joinedVia: 'baptism',
        baptismDate: '2015-03-10',
        ministryIds: ['rec'],
      },
      status: 'pending',
      submittedAt: now - 26 * hour,
      decidedAt: null,
      decidedBy: '',
      reason: '',
      memberId: null,
      linked: false,
      mergedFields: [],
      photo: null,
    },
    {
      id: 'reg3',
      data: {
        ...base,
        name: 'Otávio Nunes',
        birthDate: `${Number(today.slice(0, 4)) - 52}-09-02`,
        sex: 'M',
        phone: '11976543210',
        churchStatus: 'transferring',
        joinedVia: 'transfer',
        originChurch: 'Igreja Presbiteriana do Ipiranga',
        ministryIds: ['rec'],
      },
      status: 'rejected',
      submittedAt: now - 9 * 24 * hour,
      decidedAt: now - 8 * 24 * hour,
      decidedBy: 'Marcos Andrade',
      reason: 'Enviou o cadastro duas vezes; ficou o mais recente.',
      memberId: null,
      linked: false,
      mergedFields: [],
      photo: null,
    },
  ];
}

function sampleRegistrationLink(now: number): RegistrationLink {
  return { token: SAMPLE_REGISTRATION_TOKEN, active: true, createdAt: now - 30 * 24 * 3_600_000 };
}

const SERIES = [
  {
    id: 'dom-m',
    title: 'Culto da Manhã',
    time: '10:00',
    weekday: 0,
    ministries: ['rec', 'lou', 'mul', 'inf'],
  },
  { id: 'dom-n', title: 'Culto da Noite', time: '19:00', weekday: 0, ministries: ['rec', 'lou', 'mul'] },
  { id: 'qua', title: 'Culto de Oração', time: '19:30', weekday: 3, ministries: ['rec', 'mul'] },
];

const PAST_WEEKS = 8;
const FUTURE_WEEKS = 5;

function makeEvent(
  partial: Pick<ScheduleEvent, 'id' | 'seriesId' | 'title' | 'date' | 'time' | 'recurrence' | 'ministryIds'> &
    Partial<Pick<ScheduleEvent, 'rule'>>
): ScheduleEvent {
  return {
    rule: null,
    color: null,
    ...partial,
    need: {},
    slots: {},
    generated: false,
    published: false,
    edited: false,
    responses: {},
    attendance: {},
    nudgedAt: null,
    recorded: false,
    notices: {},
    swaps: [],
    audience: null,
  };
}

function buildEvents(weekStart: string): ScheduleEvent[] {
  const first = addDays(weekStart, -PAST_WEEKS * 7);
  const last = addDays(weekStart, FUTURE_WEEKS * 7 + 6);
  const events: ScheduleEvent[] = [];

  // A regra de cada série: semanal, a partir da primeira data dela.
  const rules = new Map(
    SERIES.map((s) => [s.id, legacyWeeklyRule(addDays(first, (s.weekday - weekdayOf(first) + 7) % 7))])
  );

  for (let d = first; d <= last; d = addDays(d, 1)) {
    for (const s of SERIES) {
      if (weekdayOf(d) !== s.weekday) continue;

      events.push(
        makeEvent({
          id: `${s.id}-${d}`,
          seriesId: s.id,
          title: s.title,
          date: d,
          time: s.time,
          recurrence: `Semanal · ${WEEKDAY_LONG[s.weekday]}`,
          rule: rules.get(s.id)!,
          ministryIds: [...s.ministries],
        })
      );
    }
  }

  const conf = addDays(weekStart, 13);
  const bat = addDays(weekStart, 28);

  events.push(
    makeEvent({
      id: `conf-${conf}`,
      seriesId: 'conf',
      title: 'Conferência de Jovens',
      date: conf,
      time: '19:30',
      recurrence: null,
      ministryIds: ['rec', 'lou', 'mul'],
    }),
    makeEvent({
      id: `bat-${bat}`,
      seriesId: 'bat',
      title: 'Culto de Batismo',
      date: bat,
      time: '16:00',
      recurrence: null,
      ministryIds: ['rec', 'mul'],
    })
  );

  return events.sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
}

/** Estado de exemplo: histórico publicado, semana atual publicada e a próxima em rascunho. */
export function createSampleState(today: string, now: number = Date.now()): DomainState {
  const weekStart = startOfWeek(today);
  const nextWeek = addDays(weekStart, 7);
  const people = members(today);
  let events = buildEvents(weekStart);

  // Histórico: gerado com folga maior, como se tivesse sido feito mês a mês — e de antes
  // da escala do casal, quando todo casal tinha a preferência de servir junto.
  const pastIds = events.filter((e) => e.date < weekStart).map((e) => e.id);
  const before = people.map((m) => ({ ...m, coupleMode: m.spouseId ? ('together' as const) : null }));

  events = generateSchedule(events, pastIds, before, MINISTRIES, {
    ministryIds: MINISTRIES.map((m) => m.id),
    disabledRoleIds: [],
    limit: 8,
    allowUnset: true,
    criteria: DEFAULT_CRITERIA,
  }).events;

  const ministries = withAdvancedRules(MINISTRIES);
  const draftIds = events
    .filter((e) => e.date >= weekStart && e.date < addDays(nextWeek, 7))
    .map((e) => e.id);
  events = generateSchedule(events, draftIds, people, ministries, {
    ministryIds: MINISTRIES.map((m) => m.id),
    disabledRoleIds: [],
    limit: SAMPLE_SETTINGS.maxPerMonth,
    allowUnset: true,
    criteria: DEFAULT_CRITERIA,
  }).events;

  // Histórico: as datas de mais de 4 semanas já foram registradas (Escalas › Histórico).
  // Publicadas: a semana atual e, no fim de semana, o que vem nos próximos 7 dias — sempre
  // há escala publicada à frente para confirmar ou trocar; o resto da próxima é rascunho.
  const recordedUntil = addDays(weekStart, -28);
  const publishedUntil = addDays(today, 7) > nextWeek ? addDays(today, 7) : nextWeek;
  events = events.map((e) => ({
    ...e,
    published: e.generated && e.date < publishedUntil,
    recorded: e.date < recordedUntil,
  }));

  // Duas situações para a demonstração: uma vaga liberada e um conflito.
  const morning = events.find((e) => e.id === `dom-m-${nextWeek}`);
  const evening = events.find((e) => e.id === `dom-n-${nextWeek}`);

  if (morning) morning.slots = { ...morning.slots, portaria: (morning.slots.portaria ?? []).slice(0, 1) };
  if (evening) {
    const rest = (evening.slots.acolhimento ?? []).filter((id) => id !== 'm5').slice(1);
    evening.slots = { ...evening.slots, acolhimento: ['m5', ...rest] };
  }

  events = events.map((e) => withSamplePresence(e, today, now));
  events = sampleAudiences(events, today, ['Marcos Andrade', 'Daniel Oliveira']);
  events = withSampleSwaps(events, people, ministries, today, now);

  // Paula lidera a Recepção (o acolhimento); Ana Silva serve no Acolhimento.
  const welcomeIds = [DEMO_LEADER_ID, ...people.filter((m) => m.name === 'Ana Silva').map((m) => m.id)];
  const sample = sampleVisitors({
    events,
    today,
    ownerIds: welcomeIds,
    inviterIds: people
      .filter((m) => m.inactiveSince === null)
      .slice(0, 12)
      .map((m) => m.id),
    staffName: people.find((m) => m.id === DEMO_LEADER_ID)?.name ?? 'Secretaria',
  });

  return {
    ministries: withSamplePairs(ministries),
    members: people,
    events,
    log: sampleLog(events, weekStart, nextWeek, now),
    settings: { ...SAMPLE_SETTINGS },
    readNotifications: [],
    onboardingHidden: false,
    registrations: [
      ...sampleRegistrations(people, today, now),
      ...(sample.referral ? [sample.referral] : []),
    ],
    registrationLink: sampleRegistrationLink(now),
    privacyRequests: samplePrivacyRequests(today, now),
    visitors: sample.visitors,
    visitorLink: { token: SAMPLE_VISITOR_TOKEN, active: true, createdAt: now - 30 * 24 * 3_600_000 },
  };
}

/** Os perfis de "Visualizar como": ficam pendentes, para "Minha agenda" mostrar "Confirmar". */
const DEMO_PROFILES = new Set([DEMO_ADMIN_ID, DEMO_LEADER_ID, DEMO_ROLE_LEADER_ID, DEMO_MEMBER_ID]);

/**
 * Pedidos de troca de exemplo, nas escalas publicadas que ainda vão acontecer: um que o
 * colega já aceitou e espera a aprovação do líder (na Recepção, fora da Portaria) e um
 * pedido para o membro da demonstração responder (ou outro perfil da demonstração, se ele
 * já está em todas as escalas publicadas).
 */
function withSampleSwaps(
  events: ScheduleEvent[],
  people: Member[],
  ministries: Ministry[],
  today: string,
  now: number
): ScheduleEvent[] {
  const lookup = buildLookup({ members: people, ministries });
  const open = events.filter((e) => e.published && e.date >= today);
  const busy = new Set<string>();
  const pick = (
    filter: (event: ScheduleEvent, roleId: string, fromId: string) => boolean,
    toFilter: (id: string) => boolean
  ) => {
    for (const event of open) {
      for (const [roleId, ids] of Object.entries(event.slots)) {
        for (const fromId of ids) {
          if (busy.has(fromId) || !filter(event, roleId, fromId)) continue;

          const to = swapCandidates(events, event.id, roleId, fromId, lookup).find(
            (c) => !c.blocked && toFilter(c.member.id) && !busy.has(c.member.id)
          );

          if (to) return { event, roleId, fromId, toId: to.member.id };
        }
      }
    }

    return null;
  };
  const toLeader = pick(
    (_event, roleId, fromId) =>
      !DEMO_PROFILES.has(fromId) && roleId !== 'portaria' && lookup.roles.get(roleId)?.ministry.id === 'rec',
    (id) => !DEMO_PROFILES.has(id)
  );

  if (toLeader) busy.add(toLeader.fromId).add(toLeader.toId);

  // Para o membro da demonstração; se ele já está em todas, para outro perfil de "Visualizar como".
  const toMember =
    pick(
      (_event, _roleId, fromId) => !DEMO_PROFILES.has(fromId),
      (id) => id === DEMO_MEMBER_ID
    ) ??
    pick(
      (_event, _roleId, fromId) => !DEMO_PROFILES.has(fromId),
      (id) => DEMO_PROFILES.has(id)
    );
  const swaps = [
    toLeader && { ...toLeader, id: 'sw-seed-1', status: 'leader' as const, at: now - 3 * 3_600_000 },
    toMember && { ...toMember, id: 'sw-seed-2', status: 'peer' as const, at: now - 2 * 3_600_000 },
  ].filter((x) => x !== null);

  return events.map((e) => {
    const mine = swaps.filter((x) => x.event.id === e.id);

    return mine.length
      ? { ...e, swaps: [...e.swaps, ...mine.map(({ event: _, ...swap }): SwapRequest => swap)] }
      : e;
  });
}

/**
 * Presença de exemplo: nos eventos passados, o check-in (quase todos presentes, uma
 * falta coberta de vez em quando); nos publicados que ainda vão acontecer, a maioria
 * confirmada e o resto pendente — os perfis da demonstração ficam pendentes, para a
 * tela mostrar "Confirmar" e "Não posso ir".
 */
function withSamplePresence(event: ScheduleEvent, today: string, now: number): ScheduleEvent {
  if (!event.published) return event;

  const entries = Object.entries(event.slots).flatMap(([roleId, ids]) => ids.map((id) => ({ roleId, id })));
  const inEvent = new Set(entries.map((x) => x.id));

  if (event.date < today) {
    const attendance: ScheduleEvent['attendance'] = {};

    for (const { roleId, id } of entries) {
      const absent = hashString(`${event.id}:${id}`) % 12 === 0;
      const substitute = absent
        ? (PEOPLE.find(
            ([pid, , , , , roles]) => !inEvent.has(pid) && Object.values(roles).flat().includes(roleId)
          )?.[0] ?? null)
        : null;

      attendance[id] = absent
        ? { status: 'absent', substituteId: substitute }
        : { status: 'present', substituteId: null };
    }

    return { ...event, attendance };
  }

  const responses: ScheduleEvent['responses'] = {};

  for (const { roleId, id } of entries) {
    if (DEMO_PROFILES.has(id) || hashString(`${event.id}:${id}`) % 4 === 0) continue;
    responses[id] = { status: 'confirmed', roleId, reason: '', at: now - (hashString(id) % 40) * 3_600_000 };
  }

  return { ...event, responses };
}

function sampleLog(events: ScheduleEvent[], weekStart: string, nextWeek: string, now: number): LogEntry[] {
  const day = 86_400_000;
  const log: LogEntry[] = [];

  events
    .filter((e) => e.generated && e.date >= weekStart)
    .forEach((e, i) => {
      log.push({
        id: `g${i}`,
        eventId: e.id,
        who: 'Marcos Andrade',
        text: 'Escala gerada automaticamente',
        at: now - 3 * day - i * 60_000,
      });

      if (e.published) {
        log.push({
          id: `p${i}`,
          eventId: e.id,
          who: 'Marcos Andrade',
          text: 'Escala publicada',
          at: now - 2 * day - i * 60_000,
        });
      }
    });

  log.push(
    {
      id: 'l3',
      eventId: `dom-n-${nextWeek}`,
      who: 'Paula Ribeiro',
      text: 'Carla Andrade escalado(a) em Acolhimento',
      at: now - 20 * 3_600_000,
    },
    {
      id: 'l4',
      eventId: `dom-m-${nextWeek}`,
      who: 'Paula Ribeiro',
      text: 'Vaga liberada em Portaria',
      at: now - 5 * 3_600_000,
    }
  );

  return log.sort((a, b) => b.at - a.at);
}

/** "Primeiro acesso": as pessoas já existem, mas sem ministérios, eventos nem disponibilidade. */
export function createEmptyState(today: string): DomainState {
  return {
    ministries: [],
    members: members(today).map((m) => ({
      ...m,
      roles: {},
      availability: Array<AvailState>(21).fill('unset'),
      blackouts: [],
    })),
    events: [],
    log: [],
    settings: { ...DEFAULT_SETTINGS },
    readNotifications: [],
    onboardingHidden: false,
    registrations: [],
    registrationLink: null,
    privacyRequests: [],
    visitors: [],
    visitorLink: null,
  };
}

/**
 * Ministérios e funções sugeridos para uma organização nova ("Começar com ministérios e
 * funções sugeridos"): os mesmos de `CHURCH_DEMO_MINISTRIES`, que a demonstração da igreja
 * também carrega, sem líderes e com ids próprios (`seed` + id).
 */
export function suggestedMinistries(seed: string): Ministry[] {
  return CHURCH_DEMO_MINISTRIES.map((m) => ({
    ...structuredClone(m),
    id: `${seed}-${m.id}`,
    leaderIds: [],
    roles: m.roles.map((r) => ({
      ...structuredClone(r),
      id: `${seed}-${r.id}`,
      leaderIds: [],
    })),
  }));
}

/**
 * Estado de uma organização recém-criada: só o Administrador (que recebe o convite), sem
 * eventos e, se pedido, com os ministérios sugeridos.
 */
export function createOrgState(
  admin: { id: string; name: string; email: string },
  options: { seed: string; suggested: boolean }
): DomainState {
  return {
    ministries: options.suggested ? suggestedMinistries(options.seed) : [],
    members: [
      {
        id: admin.id,
        inactiveSince: null,
        selfUpdatedAt: null,
        admin: true,
        photo: null,
        name: admin.name.trim(),
        nickname: '',
        email: admin.email.trim().toLowerCase(),
        phone: '',
        birthDate: null,
        sex: 'M',
        spouseId: null,
        spouseText: '',
        coupleMode: null,
        roles: {},
        availability: Array<AvailState>(21).fill('unset'),
        blackouts: [],
        ...EMPTY_PROFILE,
      },
    ],
    events: [],
    log: [],
    settings: { ...DEFAULT_SETTINGS },
    readNotifications: [],
    onboardingHidden: false,
    registrations: [],
    registrationLink: null,
    privacyRequests: [],
    visitors: [],
    visitorLink: null,
  };
}
